import * as React from 'react'
import { useQuery } from '@apollo/react-hooks'

import {
  i18n,
  Routes,
  useHorizonSessionSettings,
  useLoginCheck,
  useSiteDefinition,
} from '@thg-commerce/enterprise-core'
import {
  Currency,
  DynamicReferralWidget,
} from '@thg-commerce/enterprise-network/src/generated/graphql'
import { formatPrice } from '@thg-commerce/enterprise-pricing'
import { mq, spacing, styled, Text } from '@thg-commerce/enterprise-theme'
import {
  DynamicReferral,
  DynamicReferralProps,
} from '@thg-commerce/enterprise-widget-dynamic-referral'
import { Button } from '@thg-commerce/gravity-elements'

import { CustomerReferrals as CUSTOMER_REFERRALS_QUERY } from '../../graphql/Query/CustomerReferrals.graphql'
import { BaseWidgetProps } from '../types'

export type DynamicReferralRendererProps = BaseWidgetProps &
  Omit<DynamicReferralWidget, 'id' | 'query'>

interface Customer {
  fullName: string
  referralCode: string
  referralCount: number
  referralLink: string
  socialReferralMethods: {
    type: string
    url?: string
  }[]
  creditAccounts: {
    balance: {
      displayValue: string
    }
    currency: string
  }[]
}

const Container = styled.div`
  text-align: center;
  padding: ${spacing(5)} 0;

  ${(props) => mq(props.theme.breakpointUtils.map, 'md')} {
    padding: ${spacing(6)} 0;
  }
`

const Title = styled.h2`
  ${Text('xlarge1', 'alternate')};
  margin-bottom: ${spacing(3)};
`

const MainSubtitle = styled.p`
  ${Text('bodyText', 'default')};
  padding: 0 ${spacing(2)};
`

const StyledLoginButton = styled(Button)`
  margin-top: ${spacing(3)};
`

const DEFAULT_AMOUNT = '0.00'
const DEFAULT_LOCALE = 'en_GB'

const LoggedOutView = () => {
  const [loginUrl, setLoginUrl] = React.useState(Routes.Login)

  const i18nText = {
    title: i18n('widget.dynamicreferral.loggedout.title.text'),
    subtitle: i18n('widget.dynamicreferral.loggedout.subtitle.text'),
    login: i18n('widget.dynamicreferral.loggedout.loginbutton.title'),
  }

  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      setLoginUrl(
        `${Routes.Login}?returnTo=${encodeURIComponent(window.location.href)}`,
      )
    }
  }, [])

  return (
    <Container>
      <Title>{i18nText.title}</Title>
      <MainSubtitle data-testid="main-subtitle">
        {i18nText.subtitle}
      </MainSubtitle>
      <StyledLoginButton
        renderedAs="a"
        href={loginUrl}
        data-testid="login-button"
      >
        {i18nText.login}
      </StyledLoginButton>
    </Container>
  )
}

export const DynamicReferralRenderer = (
  props: DynamicReferralRendererProps,
) => {
  const loggedIn = useLoginCheck()
  const sessionSettings = useHorizonSessionSettings()
  const { defaultLocale = DEFAULT_LOCALE } = useSiteDefinition()

  const { data } = useQuery<
    {
      referrals: Customer
    },
    { currency: string }
  >(CUSTOMER_REFERRALS_QUERY, {
    variables: {
      currency: sessionSettings.currency as Currency,
    },
    fetchPolicy: 'no-cache',
    ssr: typeof window !== 'undefined',
    skip: !loggedIn,
  })

  if (!loggedIn || !data?.referrals) {
    return <LoggedOutView />
  }

  const customerData = data.referrals

  const currentCreditAccount =
    customerData.creditAccounts &&
    customerData.creditAccounts.find(
      (creditAccount) => creditAccount.currency === sessionSettings.currency,
    )

  const customerCreditBalance =
    currentCreditAccount?.balance?.displayValue ??
    formatPrice(
      sessionSettings.currency,
      parseFloat(DEFAULT_AMOUNT),
      defaultLocale,
    )

  const dynamicReferralProps: DynamicReferralProps = {
    customerCreditBalance,
    customerFullName: customerData.fullName || undefined,
    customerReferralCount: customerData.referralCount || 0,
    customerReferralCode: customerData.referralCode || undefined,
    customerSocialReferralMethods: customerData.socialReferralMethods?.map(
      (item) => ({
        type: item.type,
        url: item.url || undefined,
      }),
    ),
    customerReferralLink: customerData.referralLink || undefined,
    mainTitle: props.mainTitle || undefined,
    mainSubtitle: props.mainSubtitle || undefined,
    balanceNote: props.balanceNote || undefined,
    showYourCodeSection: props.showYourCodeSection || undefined,
    codeSubtitle: props.codeSubtitle || undefined,
  }

  return <DynamicReferral {...dynamicReferralProps} />
}
